import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_Row, MRT_ColumnDef } from "material-react-table"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { COMPANY_ADMIN_ROUTE, COMPANY_ADMIN_ORGANIZATION_ADMINS_CREATE_ROUTE } from "../../../../../../constants/routes"
import React, { useCallback, useMemo, useState } from "react"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { MenuItem } from "@mui/material"
import { useNavigate } from "react-router"
import { UserDetailsDialog } from "../../../../../office/admin/components/UserDetails/UserDetailsDialog"
import { CompaniesAdminsItem } from "../adminsTypes"
import { CompaniesAdminsResponse, CompanyAdminsQuery, fetchCompaniesAdmins } from "../adminsApi"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"

type Props = Readonly<{}>

function transformData(data: CompaniesAdminsResponse) {
    return data.administrators
}

export function Admins({}: Props) {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const { companyId } = useCurrentCompany()

    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [admin, setAdmin] = useState<CompaniesAdminsItem | null>()

    const companyAdmins = useApiQuery(fetchCompaniesAdmins, {
        queryName: CompanyAdminsQuery.ADMINS_FOR_COMPANY,
        dependencies: {
            companyId,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(companyAdmins)

    const columns = useMemo<MRT_ColumnDef<CompaniesAdminsItem>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-admins-table-header-name"),
                accessorKey: "user.displayName",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-admins-table-header-email"),
                accessorKey: "user.email",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("company-organization-admins-table-header-status"),
                    accessorKey: "user.isActive",
                },
                (row) => row?.user.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<CompaniesAdminsItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`details-${row.original.user.id}`}
                onClick={() => {
                    setAdmin(row.original)
                    setShowDetailsModal(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-admins-table-button-details">
                    <span>Show details</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`edit-${row.original.user.id}`}
                onClick={() => {
                    const navState = { user: row.original }
                    navigate(COMPANY_ADMIN_ORGANIZATION_ADMINS_CREATE_ROUTE, { state: navState })
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-admins-table-button-edit">
                    <span>Edit user</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`delete-${row.original.user.id}`}
                onClick={() => {
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-admins-table-button-delete">
                    <span>Delete user</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    return (
        <>
            <LocalizedStrict id="company-organization-admins-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMINS}
                    title="Administrators"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="company-organization-admins-button-create">
                            <StandardButton
                                onClick={() => {
                                    navigate(COMPANY_ADMIN_ORGANIZATION_ADMINS_CREATE_ROUTE)
                                }}
                            >
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-nav-item-account">
                            <Breadcrumb>Account</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-admins">
                            <Breadcrumb>Administrators</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
            />

            {admin && (
                <UserDetailsDialog
                    key={admin.user.id}
                    userId={admin?.user.id}
                    userType="admin"
                    open={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false)
                    }}
                />
            )}
        </>
    )
}
